import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import * as Typography from '@/components/shadcn/typography';
import { Separator } from '@/components/shadcn/separator';
import { ContentSection, normalizeForAnchor } from '@/utils/tos';
import CopyToClipboard from '@/components/ui/shared/CopyToClipboard.tsx';

interface Props {
  section: ContentSection;
  setCurrentContent: Dispatch<SetStateAction<string | null>>;
  root: React.RefObject<HTMLDivElement>;
}

export default function Section({ section, setCurrentContent, root }: Props) {
  const baseUrl =
    typeof window !== 'undefined'
      ? `${location?.origin}${location?.pathname}`
      : '';

  const { ref, entry, inView } = useInView({
    threshold: 0,
    root: root.current,
    rootMargin: '-25% 0px -75% 0px',
  });

  useEffect(() => {
    if (inView) {
      setCurrentContent(section.title);
    }
  }, [inView, entry, setCurrentContent, section.title]);

  return (
    <div
      ref={ref}
      className="flex flex-col"
      key={section.title}
      id={normalizeForAnchor(section.title)}>
      <div className="mb-3 flex items-center gap-2">
        <Typography.H4>{section.title}</Typography.H4>
        <CopyToClipboard
          content={`${baseUrl}#${normalizeForAnchor(section.title)}`}
        />
      </div>
      <div className="flex flex-col gap-4">
        {section.list && <Typography.List list={section.list} />}
        {section.content?.map((content, index) => (
          <p
            key={index.toString()}
            dangerouslySetInnerHTML={{ __html: content }}
            className="[&>a]:text-azure-500"
          />
        ))}
      </div>
      {section.nestedItems?.map(nestedItem => (
        <div className="py-3" key={nestedItem.title}>
          <Typography.H4>{nestedItem.title}</Typography.H4>
          {nestedItem.description && (
            <Typography.P>{nestedItem.description}</Typography.P>
          )}
          {nestedItem.list && <Typography.List list={nestedItem.list} />}
          {nestedItem.content?.map((content, index) => (
            <p
              key={index.toString()}
              dangerouslySetInnerHTML={{ __html: content }}
              className="[&>a]:text-azure-500"
            />
          ))}
        </div>
      ))}
      <Separator className="my-7" />
    </div>
  );
}
